import { provideHttpClient } from "@angular/common/http";
import {
  ApplicationConfig,
  InjectionToken,
  importProvidersFrom,
  provideZoneChangeDetection,
} from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { provideRouter } from "@angular/router";
import { provideKeycloak } from "keycloak-angular";

import { KeycloakConfigService } from "@core";
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
  TranslateParser,
} from "@ngx-translate/core";
import { provideAngularSvgIcon } from "angular-svg-icon";
import { environment } from "environments/environment";
import { LoggerModule } from "ngx-logger";
import { AppRoutingModule } from "./app-routing.module";
import { CoreModule } from "./core/core.module";
import { MissingTranslationHandlerService } from "./core/services/missing-translation-handler.service";
import { TranslateLoaderService } from "./core/services/translate-loader.service";
import { TranslateNfonParser } from "./shared/helpers/translate-nfon.parser";

export const APP_CONFIG = new InjectionToken<unknown>("APP_CONFIG");

export async function getAppConfig(): Promise<ApplicationConfig> {
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  const hasKeycloakParams =
    urlParams.has("state") ||
    urlParams.has("session_state") ||
    urlParams.has("code") ||
    hashParams.has("state") ||
    hashParams.has("code");

  const currentPath = window.location.pathname + window.location.search;

  // Save current route if it's not the callback or root, and no Keycloak OAuth params present
  if (
    !hasKeycloakParams &&
    currentPath !== "/auth/callback" &&
    currentPath !== "/"
  ) {
    sessionStorage.setItem("auth_redirect", currentPath);
  }

  const { createApplication } = await import("@angular/platform-browser");

  const app = await createApplication({
    providers: [provideHttpClient()],
  });

  const configService = app.injector.get(KeycloakConfigService);
  await configService.loadConfig();

  const keycloakProviders = provideKeycloak({
    config: configService.config,
    initOptions: {
      checkLoginIframe: false,
      pkceMethod: "S256",
      onLoad: "check-sso",
      redirectUri: `${window.location.origin}/`,
      flow: "standard",
    },
  });

  const sharedModules = [
    BrowserModule,
    AppRoutingModule,
    CoreModule,
    LoggerModule.forRoot(environment.logger),
    TranslateModule.forRoot({
      parser: {
        provide: TranslateParser,
        useClass: TranslateNfonParser,
      },
      loader: {
        provide: TranslateLoader,
        useClass: TranslateLoaderService,
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MissingTranslationHandlerService,
      },
      fallbackLang: "en", // specifies the language to use when a translation key is not found in the currently selected language
    }),
  ];

  return {
    providers: [
      provideHttpClient(),
      keycloakProviders,
      { provide: APP_CONFIG, useValue: configService.config },
      { provide: KeycloakConfigService, useValue: configService },
      provideRouter([]),
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideAngularSvgIcon(),
      importProvidersFrom(sharedModules),
    ],
  };
}
